import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import TopBar from '../../components/TopBar';
import {
  adminBroadcastCreate,
  adminBroadcastSend,
  adminUploadPhoto,
} from '../../api/endpoints';
import { showConfirm, showAlert, hapticNotify } from '../../lib/tg';

export default function Broadcast() {
  const [text, setText] = useState('');
  const [photoFileId, setPhotoFileId] = useState('');
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [stats, setStats] = useState<{ sent: number; total: number } | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      const created: any = await adminBroadcastCreate({
        text: text || null,
        photo_file_id: photoFileId || null,
      });
      if (!created?.id) throw new Error('no broadcast id');
      return adminBroadcastSend(created.id);
    },
    onSuccess: (r: any) => {
      hapticNotify('success');
      setStats({ sent: r?.sent ?? 0, total: r?.total ?? 0 });
    },
    onError: () => showAlert('Ошибка отправки'),
  });

  async function onSend() {
    if (!text && !photoFileId) return;
    if (await showConfirm('Отправить всем пользователям бота?')) mut.mutate();
  }

  return (
    <div className="min-h-screen pb-32">
      <TopBar title="Рассылка" />
      <div className="px-4 pt-3 space-y-3">
        <div>
          <label className="label">Текст</label>
          <textarea
            className="input min-h-32"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Текст сообщения…"
          />
        </div>
        <div>
          <label className="label">Фото (опц.)</label>
          <div className="text-xs text-muted mb-2">
            Любое соотношение сторон, ≥ 1280px по широкой стороне; JPG/PNG/WebP до 10 МБ.
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setUploading(true);
              setPhotoPreview(URL.createObjectURL(f));
              try {
                const res = await adminUploadPhoto(f);
                setPhotoFileId(res.file_id);
              } catch {
                showAlert('Ошибка загрузки');
                setPhotoPreview(null);
              } finally {
                setUploading(false);
                if (fileRef.current) fileRef.current.value = '';
              }
            }}
          />
          {photoPreview ? (
            <div className="flex items-center gap-3">
              <img src={photoPreview} className="w-28 h-20 object-cover rounded-lg2 border border-line" />
              <div className="flex flex-col gap-2">
                <button
                  className="btn-secondary text-sm"
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                >
                  {uploading ? 'Загрузка…' : 'Заменить'}
                </button>
                <button
                  className="btn-secondary text-sm"
                  onClick={() => { setPhotoFileId(''); setPhotoPreview(null); }}
                >
                  Удалить
                </button>
              </div>
            </div>
          ) : (
            <button
              className="btn-secondary w-full"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? 'Загрузка…' : 'Выбрать из галереи'}
            </button>
          )}
        </div>

        {text && (
          <div className="card p-4">
            <div className="text-xs text-muted mb-2">Превью</div>
            <div className="text-sm whitespace-pre-wrap">{text}</div>
          </div>
        )}

        {stats && (
          <div className="text-sm text-muted">Отправлено: {stats.sent} / {stats.total}</div>
        )}
      </div>
      <div className="fixed bottom-0 inset-x-0 p-4 bg-bg border-t border-line">
        <button
          className="btn-primary"
          disabled={(!text && !photoFileId) || mut.isPending || uploading}
          onClick={onSend}
        >
          Отправить всем
        </button>
      </div>
    </div>
  );
}
